import { useId, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { localTimezone } from '@/lib/time';
import { spring } from '@/lib/motion';
import { cn } from '@/lib/cn';
import { MAX_SLOTS, toLocalInput, SLOT_MAX_AHEAD_MS, SLOT_MIN_LEAD_MS, quickChips, roundMinute, slotLabel, slotTimeError } from './slots';

/** A sensible custom default: tomorrow 8pm. */
function defaultCustom(now = new Date()): string {
  const d = new Date(now);
  d.setDate(d.getDate() + 1);
  d.setHours(20, 0, 0, 0);
  return toLocalInput(d);
}

/**
 * "Offer times" (SPEC_V6_2 §5): quick chips — Tonight 9pm, Tomorrow 8pm,
 * Sat 7pm… — plus a custom time. Up to `max` times; tapping a chosen chip
 * removes it.
 */
export function SlotBuilder({
  value,
  onChange,
  max = MAX_SLOTS,
  testId = 'slots',
  chips = true,
}: {
  value: Date[];
  onChange: (v: Date[]) => void;
  max?: number;
  testId?: string;
  chips?: boolean;
}) {
  const inputId = useId();
  const [custom, setCustom] = useState(defaultCustom);
  const [err, setErr] = useState<string | null>(null);
  const quick = useMemo(() => quickChips(), []);
  const has = (d: Date) => value.some((v) => v.getTime() === d.getTime());
  const full = value.length >= max;

  const toggle = (d: Date) => {
    setErr(null);
    if (has(d)) onChange(value.filter((v) => v.getTime() !== d.getTime()));
    else if (!full) onChange([...value, d].sort((a, b) => a.getTime() - b.getTime()));
  };
  const addCustom = () => {
    const d = roundMinute(new Date(custom));
    const e = slotTimeError(d);
    if (e) return setErr(e);
    if (has(d)) return setErr('Already on your list.');
    if (full) return setErr(`Up to ${max} times.`);
    setErr(null);
    onChange([...value, d].sort((a, b) => a.getTime() - b.getTime()));
  };
  const now = Date.now();

  return (
    <div className="space-y-3" data-testid={testId}>
      {chips && (
        <div className="flex flex-wrap gap-2" role="group" aria-label="Quick times">
          {quick.map((c) => (
            <button
              key={c.id}
              type="button"
              aria-pressed={has(c.at)}
              onClick={() => toggle(c.at)}
              disabled={!has(c.at) && full}
              className={cn(
                'inline-flex h-10 items-center gap-1.5 rounded-full px-4 text-[15px] font-medium tabular-nums transition-[background-color,transform] duration-150 ease-out enabled:active:scale-[0.96] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fg disabled:cursor-not-allowed disabled:opacity-40',
                has(c.at) ? 'bg-primary text-ground' : 'bg-raised text-fg hover:bg-line',
              )}
              data-testid={`${testId}-chip-${c.id}`}
            >
              <Icon name={has(c.at) ? 'check' : 'plus'} size={16} />
              {c.label}
            </button>
          ))}
        </div>
      )}
      <div className="flex items-end gap-2">
        <div className="min-w-0 flex-1 space-y-1">
          <label htmlFor={inputId} className="text-[13px] font-medium text-muted">
            {chips ? 'Or pick a time' : 'Pick a time'}
          </label>
          <input
            id={inputId}
            type="datetime-local"
            value={custom}
            min={toLocalInput(new Date(now + SLOT_MIN_LEAD_MS))}
            max={toLocalInput(new Date(now + SLOT_MAX_AHEAD_MS - 60_000))}
            onChange={(e) => setCustom(e.target.value)}
            className="h-11 w-full rounded-[16px] border border-line bg-ground px-4 text-[16px] tabular-nums text-fg focus:outline-none focus-visible:border-fg"
            data-testid={`${testId}-custom`}
          />
        </div>
        <Button variant="subtle" icon="plus" onClick={addCustom} disabled={full} className="h-11" data-testid={`${testId}-add`}>
          Add
        </Button>
      </div>
      {err && (
        <p className="text-[15px] font-medium text-danger" role="alert" data-testid={`${testId}-error`}>
          {err}
        </p>
      )}
      {value.length > 0 && (
        <ul className="space-y-1.5" aria-label="Times you’re offering" data-testid={`${testId}-list`}>
          <AnimatePresence initial={false}>
            {value.map((d) => {
              const l = slotLabel(d.toISOString());
              return (
                <motion.li
                  key={d.getTime()}
                  layout
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={spring}
                  className="flex items-center justify-between gap-2 rounded-full bg-raised py-1 pl-4 pr-1"
                  data-testid={`${testId}-item`}
                >
                  <span className="flex items-center gap-2 text-[15px]">
                    <span className="font-semibold">{l.day}</span>
                    <span className="tabular-nums text-muted">{l.time}</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => toggle(d)}
                    className="flex h-9 w-9 items-center justify-center rounded-full text-muted transition-colors hover:bg-line hover:text-fg focus-visible:outline focus-visible:outline-2 focus-visible:outline-fg"
                    aria-label={`Remove ${l.day} ${l.time}`}
                  >
                    <Icon name="x" size={16} />
                  </button>
                </motion.li>
              );
            })}
          </AnimatePresence>
        </ul>
      )}
      <p className="text-[13px] tabular-nums text-muted">
        {value.length}/{max} · your time zone: {localTimezone()}
      </p>
    </div>
  );
}
